<template>
  <Teleport to="body">
    <!-- Desktop-only anchored tooltip (hidden on mobile) -->
    <Transition name="tour-fade">
      <div
        v-if="showTip && activeTip"
        class="fixed z-[9999] pointer-events-none hidden sm:block"
        :style="{ top: '0px', left: '0px', width: '100vw', height: '100vh' }"
      >
        <!-- Spotlight ring around the anchor -->
        <div
          v-if="anchorRect"
          class="fixed pointer-events-none rounded-lg ring-2 ring-blue-500 ring-offset-2 ring-offset-transparent transition-all duration-300"
          :style="spotlightStyle"
        />

        <!-- Backdrop (click to dismiss) -->
        <div
          class="fixed inset-0 pointer-events-auto"
          @click="dismiss"
        />

        <!-- Tooltip card -->
        <div
          class="pointer-events-auto relative bg-white rounded-xl shadow-2xl border border-gray-100 p-5 w-72"
          :style="tooltipCardStyle"
        >
          <!-- Arrow pointing up toward the anchor -->
          <div
            v-if="anchorRect"
            class="absolute -top-2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45"
            :style="arrowStyle"
          />

          <!-- Label + close -->
          <div class="flex items-center justify-between mb-1">
            <p class="text-xs font-bold text-blue-600 uppercase tracking-widest">
              {{ activeTip.tag }}
            </p>
            <button
              @click="dismiss"
              aria-label="Dismiss tip"
              class="p-1.5 -mr-1 -mt-1 text-gray-300 hover:text-gray-500 transition-colors rounded-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <h3 class="text-base font-bold text-gray-900 mb-2">{{ activeTip.title }}</h3>
          <p class="text-sm text-gray-500 leading-relaxed">{{ activeTip.content }}</p>

          <!-- Step counter + Next button -->
          <div class="flex items-center justify-between mt-4">
            <span class="text-xs text-gray-400">{{ currentStepIndex + 1 }} / {{ TIPS.length }}</span>
            <button
              v-if="hasNextTip"
              @click="advance"
              class="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-colors"
            >Next →</button>
            <button
              v-else
              @click="dismiss"
              class="text-xs font-semibold text-white bg-gray-400 hover:bg-gray-500 px-3 py-1.5 rounded-lg transition-colors"
            >Done</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

// ── Feature tip definitions ────────────────────────────────────────────────
// Each tip is shown once. The key maps to localStorage ('seenTip:<key>')
// and to data-tour="<key>" on the nav anchor element.
// Add new entries here whenever a new feature ships – users who haven't
// seen it will get the tip on their next page load.
const TIPS = [
  {
    key: 'basic-search',
    tag: 'Basic Search',
    title: 'Search Flights Your Way',
    content:
      'Search by date range instead of fixed dates, compare prices across airlines, filter by stops and currency – all in one place.',
  },
  {
    key: 'buddy-search',
    tag: 'Buddy Search',
    title: 'Find Your Perfect Meeting City',
    content:
      'Flying from different cities? Buddy Search finds the cheapest destination city that works for EVERYONE in your group – no spreadsheets needed.',
  },
  {
    key: 'explore',
    tag: 'Explore',
    title: 'Not Sure Where to Go?',
    content:
      'Browse popular destinations and get inspired. See where you can fly from your city and discover new places to visit.',
  },
  {
    key: 'price-alerts',
    tag: 'Price Alerts',
    title: 'Never Miss a Price Drop',
    content:
      "See a route you like but the price isn't right? Set a price alert – we check every 6 hours and email you the moment it drops.",
  },
]

const STORAGE_PREFIX = 'seenTip:'

const isSeenKey = (key) => !!localStorage.getItem(STORAGE_PREFIX + key)
const markSeen  = (key) => localStorage.setItem(STORAGE_PREFIX + key, 'true')

// ── State ──────────────────────────────────────────────────────────────────
const showTip   = ref(false)
const activeTip = ref(null)  // the TIPS entry currently being shown
const anchorRect = ref(null)
let tourStartTimer = null

const TOOLTIP_WIDTH = 288  // w-72
const TOOLTIP_GAP   = 12

// ── Helpers ────────────────────────────────────────────────────────────────
const getAnchorEl = (key) => {
  const els = document.querySelectorAll(`[data-tour="${key}"]`)
  for (const el of els) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return el
  }
  return null
}

const updateAnchorRect = () => {
  if (!activeTip.value) { anchorRect.value = null; return }
  const el = getAnchorEl(activeTip.value.key)
  anchorRect.value = el ? el.getBoundingClientRect() : null
}

const onResize = () => updateAnchorRect()

const handleKeydown = (e) => {
  if (e.key === 'Escape' && showTip.value) dismiss()
}

// ── Step tracking ─────────────────────────────────────────────────────────
const currentStepIndex = computed(() =>
  activeTip.value ? TIPS.findIndex(t => t.key === activeTip.value.key) : -1
)
const hasNextTip = computed(() => currentStepIndex.value < TIPS.length - 1)

// ── Dismiss ────────────────────────────────────────────────────────────────
const dismiss = () => {
  if (activeTip.value) markSeen(activeTip.value.key)
  showTip.value = false
  activeTip.value = null
  anchorRect.value = null
}

// ── Advance to next step ───────────────────────────────────────────────────
const advance = () => {
  if (activeTip.value) markSeen(activeTip.value.key)
  const nextIndex = currentStepIndex.value + 1
  if (nextIndex >= TIPS.length) { dismiss(); return }
  const next = TIPS[nextIndex]
  const el = getAnchorEl(next.key)
  activeTip.value = next
  anchorRect.value = el ? el.getBoundingClientRect() : null
  showTip.value = true
}

// ── Find and show the first unseen visible tip ─────────────────────────────
const tryShowNextTip = () => {
  if (window.innerWidth < 640) return  // desktop only

  for (const tip of TIPS) {
    if (isSeenKey(tip.key)) continue
    const el = getAnchorEl(tip.key)
    if (!el) continue  // anchor not in DOM / not visible at this viewport
    activeTip.value = tip
    anchorRect.value = el.getBoundingClientRect()
    showTip.value = true
    return
  }
}

// ── Tour scheduling ──────────────────────────────────────────────────────
const scheduleTourStart = (delay = 900) => {
  if (tourStartTimer) return
  tourStartTimer = setTimeout(() => {
    tourStartTimer = null
    tryShowNextTip()
  }, delay)
}

const resetAllTips = () => {
  TIPS.forEach(t => localStorage.removeItem(STORAGE_PREFIX + t.key))
  localStorage.removeItem('hasSeenOnboarding')
  showTip.value = false
  activeTip.value = null
  anchorRect.value = null
}

const startTour = () => {
  resetAllTips()
  const scroller = document.querySelector('.overflow-auto') || window
  scroller.scrollTo({ top: 0, behavior: 'smooth' })
  scheduleTourStart(600)
}

defineExpose({ resetAllTips, startTour })

// ── Lifecycle (no auto-start — tour only runs when user clicks Tour button)
onMounted(() => {
  window.addEventListener('resize', onResize)
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  if (tourStartTimer) clearTimeout(tourStartTimer)
  window.removeEventListener('resize', onResize)
  window.removeEventListener('keydown', handleKeydown)
})

// ── Computed styles ────────────────────────────────────────────────────────
const clamp = (val, min, max) => Math.min(Math.max(val, min), max)

const tooltipLeft = computed(() => {
  if (!anchorRect.value) return 0
  return clamp(anchorRect.value.left, 8, window.innerWidth - TOOLTIP_WIDTH - 8)
})

const tooltipTop = computed(() => {
  if (!anchorRect.value) return 0
  return anchorRect.value.bottom + TOOLTIP_GAP
})

const tooltipCardStyle = computed(() => {
  if (!anchorRect.value) {
    return { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
  }
  return {
    position: 'absolute',
    top: tooltipTop.value + 'px',
    left: tooltipLeft.value + 'px',
    width: TOOLTIP_WIDTH + 'px',
  }
})

const arrowStyle = computed(() => {
  if (!anchorRect.value) return {}
  const anchorCenterX = anchorRect.value.left + anchorRect.value.width / 2
  const left = clamp(anchorCenterX - tooltipLeft.value - 8, 8, TOOLTIP_WIDTH - 24)
  return { left: left + 'px' }
})

const spotlightStyle = computed(() => {
  if (!anchorRect.value) return {}
  const r = anchorRect.value
  return {
    top:    r.top    - 4 + 'px',
    left:   r.left   - 4 + 'px',
    width:  r.width  + 8 + 'px',
    height: r.height + 8 + 'px',
  }
})
</script>

<style scoped>
.tour-fade-enter-active,
.tour-fade-leave-active {
  transition: opacity 0.25s ease;
}
.tour-fade-enter-from,
.tour-fade-leave-to {
  opacity: 0;
}
</style>
